import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import "./index.css";

// 16 EN 15804+A2 characterization factors
const INDICATORS = [
  { id: "GWP-total", label: "GWP-total", unit: "kg CO₂ eq" },
  { id: "GWP-fossil", label: "GWP-fossil", unit: "kg CO₂ eq" },
  { id: "GWP-biogenic", label: "GWP-biogenic", unit: "kg CO₂ eq" },
  { id: "GWP-luluc", label: "GWP-luluc", unit: "kg CO₂ eq" },
  { id: "ODP", label: "ODP", unit: "kg CFC-11 eq" },
  { id: "AP", label: "AP", unit: "mol H⁺ eq" },
  { id: "EP-freshwater", label: "EP-freshwater", unit: "kg P eq" },
  { id: "EP-marine", label: "EP-marine", unit: "kg N eq" },
  { id: "EP-terrestrial", label: "EP-terrestrial", unit: "mol N eq" },
  { id: "POCP", label: "POCP", unit: "kg NMVOC eq" },
  { id: "ADPE", label: "ADPE", unit: "kg Sb eq" },
  { id: "ADPF", label: "ADPF", unit: "MJ" },
  { id: "WDP", label: "WDP", unit: "m³ world eq deprived" },
  { id: "PM", label: "PM", unit: "disease incidence" },
  { id: "IRP", label: "IRP", unit: "kBq U²³⁵ eq" },
  { id: "ETP-fw", label: "ETP-fw", unit: "CTUe" },
];

const ALL_PHASES_STD = ["A1", "A2", "A3", "A4", "A5", "B1", "B2", "B3", "B4", "B5", "B6", "B7", "C1", "C2", "C3", "C4", "D"];
const ALL_PHASES_C2C = ["A1", "A2", "A3", "A4", "A5", "B1", "B2", "B3", "B4", "B5", "B6", "B7", "C1", "C2", "C3", "C4_tech", "C4_bio", "D"];

const PHASE_DISPLAY_LABEL = {
  C4_tech: "C4a",
  C4_bio: "C4b",
};

const PHASE_GROUP_COLOR = {
  A1: "lca-results-phase-product",
  A2: "lca-results-phase-product",
  A3: "lca-results-phase-product",
  A4: "lca-results-phase-product",
  A5: "lca-results-phase-product",
  B1: "lca-results-phase-use",
  B2: "lca-results-phase-use",
  B3: "lca-results-phase-use",
  B4: "lca-results-phase-use",
  B5: "lca-results-phase-use",
  B6: "lca-results-phase-use",
  B7: "lca-results-phase-use",
  C1: "lca-results-phase-eol",
  C2: "lca-results-phase-eol",
  C3: "lca-results-phase-eol",
  C4: "lca-results-phase-eol",
  C4_tech: "lca-results-phase-eol",
  C4_bio: "lca-results-phase-eol",
  D: "lca-results-phase-beyond",
};

const formatScientific = (value) => {
  if (value === null || value === undefined) return "MND";
  if (value === 0) return "0";
  const numValue = Number(value);
  if (isNaN(numValue)) return "MND";
  const exp = Math.floor(Math.log10(Math.abs(numValue)));
  const mantissa = numValue / Math.pow(10, exp);
  const sign = numValue < 0 ? "-" : "";
  return `${sign}${Math.abs(mantissa).toFixed(2)}E${exp >= 0 ? "+" : ""}${String(exp).padStart(2, "0")}`;
};

const LCAResultsMatrix = ({ productId, selectedPhases, lcaScope, lcaResults, productInfo }) => {
  const { t } = useTranslation();

  const isCradleToCradle = lcaScope === "cradle-to-cradle";
  const allPhases = isCradleToCradle ? ALL_PHASES_C2C : ALL_PHASES_STD;

  // Filter to only phases that are checked
  const activePhases = useMemo(() => {
    return allPhases.filter((p) => selectedPhases?.[p] === true);
  }, [selectedPhases, allPhases]);

  // Get product info for header - prioritize from props, fallback to API response
  const apiProductInfo = lcaResults?.product_info || {};
  const functionalUnit = productInfo?.functional_unit || apiProductInfo.functional_unit || 
    (productInfo?.fu_amount && productInfo?.fu_unit ? `${productInfo.fu_amount} ${productInfo.fu_unit}` : "1 functional unit");
  const productName = productInfo?.product_name || apiProductInfo.product_name || "";
  const versionInfo = lcaResults?.version_info || {};

  if (activePhases.length === 0) {
    return (
      <div className="lca-results-empty">
        <div className="text-center text-muted-foreground text-sm">
          {t("NO_PHASES_SELECTED") || "No lifecycle phases selected — select phases above to see results."}
        </div>
      </div>
    );
  }

  return (
    <div className="lca-results-matrix-container">
      {/* Green header */}
      <div className="lca-results-header">
        <span className="lca-results-header-text">
          {t("LCA_RESULTS_HEADER") || "RESULTS OF THE LCA – ENVIRONMENTAL IMPACT according to EN 15804+A2"}
          {productName && `: ${productName}`}
          {functionalUnit && ` (${functionalUnit})`}
        </span>
      </div>

      <div className="lca-results-table-wrapper">
        <table className="lca-results-table">
          <thead>
            <tr className="lca-results-table-header">
              <th className="lca-results-col-parameter sticky-left">
                {t("PARAMETER") || "Parameter"}
              </th>
              <th className="lca-results-col-unit">
                {t("UNIT") || "Unit"}
              </th>
              {activePhases.map((phase) => (
                <th
                  key={phase}
                  className={`lca-results-col-phase ${PHASE_GROUP_COLOR[phase] || ""}`}
                >
                  {PHASE_DISPLAY_LABEL[phase] || phase}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {INDICATORS.map((ind, rowIdx) => (
              <tr
                key={ind.id}
                className={rowIdx % 2 === 0 ? "lca-results-row-even" : "lca-results-row-odd"}
              >
                <td className="lca-results-col-parameter sticky-left">
                  {ind.label}
                </td>
                <td className="lca-results-col-unit">
                  {ind.unit}
                </td>
                {activePhases.map((phase) => {
                  const phaseData = lcaResults?.phase_results?.[phase];
                  const val = phaseData ? phaseData[ind.id] : null;
                  const isNull = val === null || val === undefined;
                  const isNeg = typeof val === "number" && val < 0;
                  return (
                    <td
                      key={phase}
                      className={`lca-results-col-value ${isNeg ? "lca-results-negative" : ""} ${isNull ? "lca-results-mnd" : ""}`}
                    >
                      {isNull ? "MND" : formatScientific(val)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="lca-results-legend">
        <div className="lca-results-legend-text">
          {t("LCA_RESULTS_LEGEND") || 
            "GWP = Global warming potential; ODP = Ozone depletion potential; AP = Acidification potential; EP = Eutrophication potential; " +
            "POCP = Photochemical ozone creation potential; ADPE = Abiotic depletion – elements; ADPF = Abiotic depletion – fossil; " +
            "WDP = Water deprivation potential; PM = Particulate matter; IRP = Ionising radiation; ETP-fw = Ecotoxicity freshwater. " +
            "MND = Module not declared."}
        </div>
      </div>
    </div>
  );
};

export default LCAResultsMatrix;

